import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { PlainInput2 } from 'components/PlainInput/PlainInput2'
import { transactionTypeOptions } from 'config'
import { useProductTypeOptions } from 'hooks/useProductTypeOptions'
import { convertNullToBlank, convertNullToBlankValueToDecimal } from 'pages/Resources/GuidelineOverlay/logic'
import { useEffect, useMemo, useState } from 'react'
import { Modal, Select } from 'stories/components'
import { confirm } from 'utils'

import { IMinMaxLimitPerTypes } from '../interface'

interface IProps {
  data: IMinMaxLimitPerTypes[]
  isOpen: boolean
  loading: string
  selectedKey: string
  onClose: () => void
  onSubmit: (data: any[]) => void
}

export const MinMaxLimitPerTypes = (props: IProps) => {
  const { data, selectedKey, loading, isOpen, onClose, onSubmit } = props
  const { optionsMap: productTypeOptionsMap, label: productTypeLabel } = useProductTypeOptions()

  const [selected, setSelected] = useState<IMinMaxLimitPerTypes[]>([])

  const fieldName = selectedKey.includes('commitment') ? 'Commitment Fee' : 'Due Dilligence'

  useEffect(() => {
    setSelected(data)
  }, [data])

  const onAdd = () => {
    const newData = cloneDeep(selected)
    newData.push({
      productType: '',
      transactionType: '',
      loanAmount: { from: NaN, to: NaN },
      minAmount: NaN,
      maxAmount: NaN,
      minPercent: NaN,
      maxPercent: NaN,
    })
    setSelected(newData)
  }

  const onRemove = async (index: number) => {
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Do you want to delete this row?
        <br />
        <span className="text-gray-600 text-base">
          Product Type: {productTypeLabel(selected[index].productType) || 'N/A'}
        </span>
        <br />
        <span className="text-gray-600 text-base">Transaction Type: {selected[index].transactionType || 'N/A'}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    const newData = cloneDeep(selected)
    newData.splice(index, 1)
    setSelected(newData)
  }

  const onChangeOrder = (val: string, index: number) => {
    const nth = Number(val) - 1
    if (nth === index) return

    const newData = cloneDeep(selected)
    const removedOne = newData.splice(index, 1)
    newData.splice(nth, 0, removedOne[0])
    setSelected(newData)
  }

  const onChangeType = (index: number, key: 'productType' | 'transactionType', value: any) => {
    const newData = cloneDeep(selected)
    if (newData[index][key] == value) return
    newData[index][key] = value
    setSelected(newData)
  }

  const onChangeLimit = (index: number, key: 'minAmount' | 'maxAmount' | 'minPercent' | 'maxPercent', value: any) => {
    const newData = cloneDeep(selected)
    if (value == '') value = NaN
    else value = isNaN(Number(value)) ? NaN : Number(value)
    if (newData[index][key] == value) return
    newData[index][key] = value
    setSelected(newData)
  }

  const onChangeLoanAmount = (index: number, key: 'from' | 'to', value: any) => {
    const newData = cloneDeep(selected)
    if (value == '') value = NaN
    else value = isNaN(Number(value)) ? NaN : Number(value)
    if (newData[index].loanAmount[key] == value) return
    newData[index].loanAmount[key] = value
    setSelected(newData)
  }

  const rederTable = useMemo(() => {
    return (
      <table className="w-full">
        <thead>
          <tr>
            <th rowSpan={2} className="p-1 border">
              Product Type
            </th>
            <th rowSpan={2} className="p-1 border">
              Transaction Type
            </th>
            <th colSpan={2} className="p-1 border">
              Loan Amount
            </th>
            <th rowSpan={2} className="p-1 border w-24">
              Min Amount
            </th>
            <th rowSpan={2} className="p-1 border w-24">
              Max Amount
            </th>
            <th rowSpan={2} className="p-1 border w-24">
              Min Percent
            </th>
            <th rowSpan={2} className="p-1 border w-24">
              Max Percent
            </th>
            <th rowSpan={2} className="p-1 border">
              Action
            </th>
          </tr>
          <tr>
            <th className="p-1 border w-32">From</th>
            <th className="p-1 border w-32">To</th>
          </tr>
        </thead>
        <tbody>
          {selected.map((item, index) => {
            const orderOptions = selected.map((_, idx) => (idx + 1).toString())
            return (
              <tr key={index}>
                <td className="border p-1">
                  <Select
                    id="productType"
                    options={productTypeOptionsMap}
                    hasDefaultOption={true}
                    value={item.productType}
                    className="w-full mb-[-16px]"
                    onChange={(value: any) => onChangeType(index, 'productType', value)}
                  />
                </td>
                <td className="border p-1">
                  <Select
                    id="transactionType"
                    options={transactionTypeOptions}
                    hasDefaultOption={true}
                    value={item.transactionType}
                    className="w-full mb-[-16px]"
                    onChange={(value: any) => onChangeType(index, 'transactionType', value)}
                  />
                </td>
                <td className="p-1 border">
                  <div className="flex justify-center text-center">
                    <PlainInput2
                      value={convertNullToBlank(item.loanAmount.from)}
                      content={convertNullToBlankValueToDecimal(item.loanAmount.from)}
                      className="w-full"
                      onChange={(value: any) => onChangeLoanAmount(index, 'from', value)}
                    />
                  </div>
                </td>
                <td className="p-1 border">
                  <div className="flex justify-center text-center">
                    <PlainInput2
                      value={convertNullToBlank(item.loanAmount.to)}
                      content={convertNullToBlankValueToDecimal(item.loanAmount.to)}
                      className="w-full"
                      onChange={(value: any) => onChangeLoanAmount(index, 'to', value)}
                    />
                  </div>
                </td>
                <td className="p-1 border">
                  <div className="flex justify-center text-center">
                    <PlainInput2
                      value={convertNullToBlank(item.minAmount)}
                      content={convertNullToBlankValueToDecimal(item.minAmount)}
                      className="w-full"
                      onChange={(value: any) => onChangeLimit(index, 'minAmount', value)}
                    />
                  </div>
                </td>
                <td className="p-1 border">
                  <div className="flex justify-center text-center">
                    <PlainInput2
                      value={convertNullToBlank(item.maxAmount)}
                      content={convertNullToBlankValueToDecimal(item.maxAmount)}
                      className="w-full"
                      onChange={(value: any) => onChangeLimit(index, 'maxAmount', value)}
                    />
                  </div>
                </td>
                <td className="p-1 border">
                  <div className="flex justify-center text-center">
                    <PlainInput2
                      value={convertNullToBlank(item.minPercent)}
                      content={convertNullToBlankValueToDecimal(item.minPercent)}
                      className="w-full"
                      onChange={(value: any) => onChangeLimit(index, 'minPercent', value)}
                    />
                  </div>
                </td>
                <td className="p-1 border">
                  <div className="flex justify-center text-center">
                    <PlainInput2
                      value={convertNullToBlank(item.maxPercent)}
                      content={convertNullToBlankValueToDecimal(item.maxPercent)}
                      className="w-full"
                      onChange={(value: any) => onChangeLimit(index, 'maxPercent', value)}
                    />
                  </div>
                </td>
                <td className="p-1 border">
                  <div className="flex justify-center items-center gap-2">
                    <select
                      onChange={(e) => onChangeOrder(e.target.value, index)}
                      value={index + 1}
                      className="block rounded py-1.5 px-2 w-16 text-sm text-gray-900 bg-transparent border border-gray-200 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
                    >
                      {orderOptions.map((val) => (
                        <option key={val}>{val}</option>
                      ))}
                    </select>

                    <div
                      className="w-fit p-1 transition-all duration-200 hover:cursor-pointer hover-shadow1 rounded"
                      onClick={() => onRemove(index)}
                    >
                      <TrashIcon className="w-4 h-4 hover:cursor-pointer text-red-700" />
                    </div>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    )
  }, [selected])

  return (
    <Modal
      title={`Edit ${fieldName}`}
      titleOkay="Confirm"
      loading={loading === 'setting'}
      isOpen={isOpen}
      onClose={onClose}
      onOk={() => onSubmit(selected)}
    >
      <div>
        {rederTable}
        <div className="flex justify-end mt-2">
          <div
            className="flex items-center gap-2 cursor-pointer w-fit text-shade-blue transition-all duration-200 hover:border-b hover:border-b-shade-blue pr-1 h-6"
            onClick={onAdd}
          >
            <PlusIcon className="w-4 h-4" />
            <span>Add</span>
          </div>
        </div>
      </div>
    </Modal>
  )
}
